import { Permitted } from "@suse-o11y/permitted";
import { prop } from "@suse-o11y/prop";
import { type Loading, RemoteData, type Success } from "@suse-o11y/remote-data";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { type QueryView, isNodeOwned } from "~/models/dto";

import { useQueryViewBySlug } from "../../../../common/use-query-view-by-slug";
import { useUserProfile } from "../../../../common/use-user-profile";
import { useNavigateFrom } from "../../../../components/react/link";
import { queryPresentationFilters } from "../../../../queries/presentation-filters";
import { useQuery } from "../../../../queries/react-query";
import {
	selectAllSearchParams,
	selectComponentIdentifier,
	selectIsComponentPage,
	selectViewSlug,
} from "../../route/RouteSelectors";

import { mergeDetachedFiltersIntoViewQuery } from "./ViewPointHelpers";
import {
	DEFAULT_COMPONENT_VIEW_POINT,
	DEFAULT_VIEW_POINT,
	DEFAULT_VIEW_REPLACEMENT_QUERY,
	diffViewShareableState,
	generateExploreSubviewQuery,
	generateViewComponentQuery,
	mergeViewShareableState,
} from "./ViewPointQueryUtils";

interface OriginViewOptions {
	/**
	 * Whether to compare the current initial view against the origin view instead of the current view.
	 * When `true` an origin view of an explore subview is dirty when either `?originView` or `?originQuery` is present.
	 * When `false` a view or an exploration subview is dirty when `?view` or `?query` is different than initial view.
	 *
	 * @default false
	 */
	isOriginView?: boolean;
}

const DEFAULT_ORIGIN_VIEW_OPTIONS: OriginViewOptions = {
	isOriginView: false,
};

/**
 * Hook that retrieves the list of supported filter names for the current view (resolved from the URL view slug).
 *
 * @returns either:
 *   - `Loading` while the view query is loading
 *   - `Success<undefined>` when there is no view slug in the URL
 *   - `Success<Array<string>>` containing the prefixes of label-prefixed filters supported by the current view type
 */
export const useCurrentViewSupportedFilterNames = (): Loading | Success<undefined | Array<string>> => {
	const viewSlug = useSelector(selectViewSlug);
	const currentViewIdentifier = useQueryViewBySlug(viewSlug)?.transform(prop("identifier")).getOrElse(undefined);
	const { data: presentationFilters, isLoading } = useQuery({
		enabled: currentViewIdentifier != null,
		select: prop("filters"),
		...queryPresentationFilters(currentViewIdentifier ?? ""),
	});

	return useMemo(() => {
		if (viewSlug == null) {
			return RemoteData.success(undefined);
		}

		if (currentViewIdentifier == null || isLoading) {
			return RemoteData.loading();
		}

		return RemoteData.success(presentationFilters?.map(prop("tagKey")));
	}, [viewSlug, currentViewIdentifier, presentationFilters, isLoading]);
};

export const useCurrentView = (options?: UseCurrentInitialViewOptions): Permitted<QueryView> => {
	const currentInitialView = useCurrentInitialView(options);
	const currentViewQuery = useCurrentViewQuery();
	const { view } = useSelector(selectAllSearchParams);
	const [hideUnavailableConnections] = useUserProfile({
		select: prop("hideUnavailableConnections"),
	});

	return useMemo(
		() =>
			Permitted.all({
				currentQuery: currentViewQuery,
				currentInitial: currentInitialView,
			}).transform(({ currentQuery, currentInitial }) => {
				const normalizedView =
					view?.connectedComponents === undefined && hideUnavailableConnections
						? { ...view, query: currentQuery, connectedComponents: false }
						: { ...view, query: currentQuery };

				return mergeViewShareableState(currentInitial, normalizedView);
			}),
		[currentInitialView, currentViewQuery, hideUnavailableConnections, view],
	);
};

export interface UseCurrentInitialViewOptions extends OriginViewOptions {}

export const useCurrentInitialView = ({
	isOriginView,
}: UseCurrentInitialViewOptions = DEFAULT_ORIGIN_VIEW_OPTIONS): Permitted<QueryView> => {
	const currentViewInitialQuery = useCurrentInitialViewQuery();
	const viewSlug = useSelector(selectViewSlug);
	// when componentView we always want to override the current state with the default state for the component view
	// this default view point is only used in subview. The main component view has an override for itself in the currentView,
	// which ignores all view settings for that view.
	const defaultView = useSelector(selectIsComponentPage) ? DEFAULT_COMPONENT_VIEW_POINT : DEFAULT_VIEW_POINT;
	const currentSavedView = useQueryViewBySlug(viewSlug);
	const { originView, explore } = useSelector(selectAllSearchParams);

	const isExploreView = explore != null && !isOriginView;

	return useMemo(
		() =>
			Permitted.all({
				currentInitialQuery: currentViewInitialQuery,
				view: currentSavedView ?? Permitted.granted(defaultView),
			}).transform(({ currentInitialQuery, view }) =>
				mergeViewShareableState(isExploreView ? omit(view, ["viewType"]) : view, {
					...originView,
					query: currentInitialQuery,
				}),
			),
		[currentViewInitialQuery, originView, isExploreView, currentSavedView, defaultView],
	);
};

export const useCurrentInitialViewQuery = (): Permitted<string> => {
	const viewSlug = useSelector(selectViewSlug);
	const componentIdentifier = useSelector(selectComponentIdentifier);
	const { explore } = useSelector(selectAllSearchParams);
	const currentSavedView = useQueryViewBySlug(viewSlug);

	return useMemo(() => {
		if (explore != null) {
			return Permitted.granted(generateExploreSubviewQuery(explore) ?? DEFAULT_VIEW_POINT.query);
		}

		// components has a specific query selecting the component
		if (componentIdentifier != null) {
			return Permitted.granted(generateViewComponentQuery(componentIdentifier));
		}

		return currentSavedView?.transform(prop("query")) ?? Permitted.granted(DEFAULT_VIEW_POINT.query);
	}, [componentIdentifier, currentSavedView, explore]);
};

/**
 * Get the current view query, the query depends on the current selection and depending on current selections it will include:
 * - The query from the saved view, extended with detached filters when applicable
 * - The query for a specific explore subview (like the component view)
 * - The user defined query (on top of either the subview query or the saved view query)
 */
export const useCurrentViewQuery = ({
	includeDetachedFilters = true,
}: {
	includeDetachedFilters?: boolean;
} = {}): Permitted<string> => {
	const currentViewInitialQuery = useCurrentInitialViewQuery();
	const viewSlug = useSelector(selectViewSlug);
	const currentSavedView = useQueryViewBySlug(viewSlug);
	const { explore, query, detachedFilters } = useSelector(selectAllSearchParams);

	const supportedFilters = useCurrentViewSupportedFilterNames();

	return useMemo(
		() =>
			currentViewInitialQuery.chain((initial) => {
				if (explore != null || detachedFilters == null || currentSavedView == null) {
					return Permitted.granted(query ?? initial);
				}

				return currentSavedView.transform((savedView) => {
					if (!isNodeOwned(savedView)) {
						return query ?? initial;
					}

					return mergeDetachedFiltersIntoViewQuery({
						query: query ?? initial,
						detachedFilters: includeDetachedFilters ? detachedFilters : [],
						supportedFilters: supportedFilters.getOrElse(undefined) ?? [],
					});
				});
			}),
		[
			currentSavedView,
			currentViewInitialQuery,
			detachedFilters,
			explore,
			query,
			supportedFilters,
			includeDetachedFilters,
		],
	);
};

export const useQueryToExecute = (): Permitted<string> => {
	const currentQuery = useCurrentViewQuery();

	return useMemo(() => currentQuery.transform((query) => query || DEFAULT_VIEW_REPLACEMENT_QUERY), [currentQuery]);
};

// When someone copy/pastes a URL to StackState that includes a bunch of detached filters,
// only the detached filters that are applicable to the current view should be used, the
// rest should be ignored.
export const useDropDetachedFiltersOnFirstLoad = (): void => {
	const hasAlreadyDroppedFilters = useRef(false);
	const supportedFilters = useCurrentViewSupportedFilterNames();
	const navigateFrom = useNavigateFrom();

	useEffect(() => {
		if (!hasAlreadyDroppedFilters.current && supportedFilters.isSuccess()) {
			hasAlreadyDroppedFilters.current = true;

			navigateFrom("*", {
				method: "replace",
				search: (search) => {
					// Drop all filters that are not supported by the current view, when copy/pasting a url
					// the intention is to only share the selection for that view
					const updatedFilters = search.detachedFilters?.filter((filter) =>
						supportedFilters.value?.some((supportedName) => filter.startsWith(`${supportedName}:`)),
					);
					return { ...search, detachedFilters: updatedFilters };
				},
			});
		}
	}, [navigateFrom, supportedFilters]);
};

export interface UseIsCurrentViewDirtyOptions extends OriginViewOptions {}

export const useIsCurrentViewDirty = ({
	isOriginView,
}: UseIsCurrentViewDirtyOptions = DEFAULT_ORIGIN_VIEW_OPTIONS): Permitted<boolean> => {
	const currentInitialView = useCurrentInitialView();
	const currentViewQuery = useCurrentViewQuery({
		includeDetachedFilters: false,
	});

	const { explore, view, originView, originQuery } = useSelector(selectAllSearchParams);

	return useMemo(() => {
		if (isOriginView && explore != null) {
			return Permitted.granted(!(isEmpty(originQuery) && isEmpty(originView)));
		}

		return Permitted.all({
			currentInitial: currentInitialView,
			currentQuery: currentViewQuery,
		}).transform(({ currentInitial, currentQuery }) => {
			const diff = diffViewShareableState(currentInitial, { ...view, query: currentQuery });

			return !isEmpty(diff);
		});
	}, [currentInitialView, currentViewQuery, explore, view, originView, originQuery, isOriginView]);
};
